import { Service } from './Service';
import http from './ApiConfig';
import { AdicionarSugestaoRespostaInputType } from '@/types/sugestoesRespostas/AdicionarSugestaoRespostaInputType';
import { ObterSugestaoRespostaOutputType } from '@/types/sugestoesRespostas/ObterSugestaoRespostaOutputType';
import { RespostaSugestaoRespostaOutputType } from '@/types/sugestoesRespostas/RespostaSugestaoRespostaOutputType';

export class SugestaoRespostaService extends Service {
  constructor() {
    super('SugestoesRespostas');
  }

  async obterSugestaoRespostaPorId(id: number): Promise<ObterSugestaoRespostaOutputType> {
    try {
      if (id <= 0) {
        throw new Error('ID deve ser um valor positivo válido.');
      }

      const url = `${this._nomeControle}/${id}`;
      const configuracoes = this.GetHeader();
      const response = await http.get<ObterSugestaoRespostaOutputType>(url, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter sugestão de resposta por ID ${id}:`, error);
      throw error;
    }
  }

  async obterSugestoesRespostasPorCampanhaId(campanhaId: number): Promise<ObterSugestaoRespostaOutputType[]> {
    try {
      if (campanhaId <= 0) {
        throw new Error('ID da campanha deve ser um valor positivo válido.');
      }

      const url = `${this._nomeControle}/campanha/${campanhaId}`;
      const configuracoes = this.GetHeader();
      const response = await http.get<ObterSugestaoRespostaOutputType[]>(url, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter sugestões de respostas por ID da campanha ${campanhaId}:`, error);
      throw error;
    }
  }

  async obterSugestoesRespostasPorUsuarioId(usuarioId: string): Promise<ObterSugestaoRespostaOutputType[]> {
    try {
      if (!usuarioId || usuarioId.trim() === '') {
        throw new Error('ID do usuário é obrigatório.');
      }

      const url = `${this._nomeControle}/usuario/${usuarioId}`;
      const configuracoes = this.GetHeader();
      const response = await http.get<ObterSugestaoRespostaOutputType[]>(url, configuracoes);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter sugestões de respostas por ID do usuário ${usuarioId}:`, error);
      throw error;
    }
  }

  async adicionarSugestaoResposta(novaSugestaoResposta: AdicionarSugestaoRespostaInputType): Promise<RespostaSugestaoRespostaOutputType> {
    try {
      if (!novaSugestaoResposta) {
        throw new Error('Dados da sugestão de resposta são obrigatórios.');
      }

      if (novaSugestaoResposta.sugestaoCampanhaId <= 0) {
        throw new Error('ID da sugestão de campanha deve ser um valor positivo válido.');
      }

      if (!novaSugestaoResposta.usuarioId || novaSugestaoResposta.usuarioId.trim() === '') {
        throw new Error('ID do usuário é obrigatório.');
      }

      if (!novaSugestaoResposta.resposta || novaSugestaoResposta.resposta.trim() === '') {
        throw new Error('Resposta é obrigatória.');
      }

      if (novaSugestaoResposta.pontuacao < 0) {
        throw new Error('Pontuação deve ser um valor não negativo.');
      }

      const url = `${this._nomeControle}`;
      const configuracoes = this.GetHeader();
      const response = await http.post<RespostaSugestaoRespostaOutputType>(url, novaSugestaoResposta, configuracoes);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar sugestão de resposta:', error);
      throw error;
    }
  }

  private validarIdPositivo(id: number): boolean {
    return id > 0;
  }

  private validarStringObrigatoria(valor: string): boolean {
    return valor && valor.trim().length > 0;
  }

  private validarPontuacao(pontuacao: number): boolean {
    return pontuacao >= 0;
  }

  validarSugestaoResposta(sugestaoResposta: AdicionarSugestaoRespostaInputType): string[] {
    const erros: string[] = [];

    if (!sugestaoResposta) {
      erros.push('Dados da sugestão de resposta são obrigatórios.');
      return erros;
    }

    if (!this.validarIdPositivo(sugestaoResposta.sugestaoCampanhaId)) {
      erros.push('ID da sugestão de campanha deve ser um valor positivo válido.');
    }

    if (!this.validarStringObrigatoria(sugestaoResposta.usuarioId)) {
      erros.push('ID do usuário é obrigatório.');
    }

    if (!this.validarStringObrigatoria(sugestaoResposta.resposta)) {
      erros.push('Resposta é obrigatória.');
    }

    if (!this.validarPontuacao(sugestaoResposta.pontuacao)) {
      erros.push('Pontuação deve ser um valor não negativo.');
    }

    return erros;
  }
}
